// Brand slice 1 (D157) — the identity kit: DOM builders for the signature components the design
// handoff (design_handoff_kolony_identity) defines once and the whole product reuses. Pixels live in
// theme.css (classes carry the hover/active/capture states cssText cannot express); these builders own
// structure and text. textContent only (§15.1) — user strings can never become markup. The svgIcon
// register is the start of the flat inline-SVG set that replaces OS emoji product-wide.

const SVG_NS = 'http://www.w3.org/2000/svg';

/** Namespace-aware SVG builder (innerHTML is banned — §15.1). Shared by the kit and the home screen. */
export function svgEl<T extends SVGElement>(doc: Document, tag: string, attrs: Record<string, string | number>): T {
  const e = doc.createElementNS(SVG_NS, tag) as T;
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, String(v));
  return e;
}

export type KeycapState = 'capture' | 'unbound';

/** The key-cap chip. Variable width by content; `—` auto-reads as unbound (keyLabel('') renders it). */
export function keycap(doc: Document, label: string, opts: { state?: KeycapState; small?: boolean } = {}): HTMLSpanElement {
  const k = doc.createElement('span');
  k.className = 'kolony-keycap';
  if (opts.small) k.classList.add('kolony-keycap--sm');
  const state = opts.state ?? (label === '—' ? 'unbound' : undefined);
  if (state) k.classList.add(`kolony-keycap--${state}`);
  k.textContent = label;
  return k;
}

/** The letter-spaced caps motif (STAMINA · KICK-OFF · CONTROLS), formalised. */
export function capsTag(doc: Document, label: string, variant: 'quiet' | 'boxed' | 'loud'): HTMLSpanElement {
  const t = doc.createElement('span');
  t.className = 'kolony-tag';
  if (variant !== 'quiet') t.classList.add(`kolony-tag--${variant}`);
  t.textContent = label;
  return t;
}

/** The ONE element allowed at primary weight on a screen (amber, display face). */
export function primaryBtn(doc: Document, label: string, onClick: () => void): HTMLButtonElement {
  const b = doc.createElement('button');
  b.type = 'button';
  b.className = 'kolony-btn-primary';
  b.textContent = label;
  b.onclick = onClick;
  return b;
}

export interface SecondaryBtn {
  readonly el: HTMLButtonElement;
  /** Update the badge pill; 0 hides it (the "N new" count is computed, never stored). */
  setBadge(n: number): void;
}

/** Secondary tier — quiet plate, amber on hover; optional "N new" badge pill. */
export function secondaryBtn(doc: Document, label: string, onClick: () => void, badgeCount?: number): SecondaryBtn {
  const b = doc.createElement('button');
  b.type = 'button';
  b.className = 'kolony-btn-secondary';
  const text = doc.createElement('span');
  text.textContent = label;
  b.append(text);
  b.onclick = onClick;
  let badge: HTMLSpanElement | null = null;
  const setBadge = (n: number): void => {
    if (!badge) return;
    badge.textContent = `${n} new`;
    badge.style.display = n > 0 ? '' : 'none';
  };
  if (badgeCount !== undefined) {
    badge = doc.createElement('span');
    badge.className = 'kolony-badge';
    b.append(badge);
    setBadge(badgeCount);
  }
  return { el: b, setBadge };
}

/**
 * The Trade Seal — the brand mark (D198). RASTER on purpose, unlike everything else in this
 * kit: it is a painting in the same hand as the rank emblems and the board art, and neither
 * the rope rim nor the five goods survive being redrawn as flat vector.
 *
 * `alt` is deliberately empty. The mark sits directly above the KOLONY wordmark, which is
 * real text; naming it here would make every screen reader say the game's name twice.
 * Width and height are set as ATTRIBUTES as well as CSS so the lockup below cannot jump
 * when the webp lands.
 */
export function sealMark(doc: Document, size: number): HTMLImageElement {
  const img = doc.createElement('img');
  img.className = 'kolony-seal';
  img.src = '/icons/logo-seal.webp';
  img.width = size;
  img.height = size;
  img.alt = '';
  img.decoding = 'async';
  return img;
}

/**
 * The wordmark lockup. `centred` (home): KOLONY at display XL, the tagline between two amber rules.
 * `left` (panels): the rule sits left of the tagline only.
 */
export function wordmarkLockup(doc: Document, mode: 'centred' | 'left'): HTMLDivElement {
  const w = doc.createElement('div');
  w.className = mode === 'centred' ? 'kolony-wordmark kolony-wordmark--centred' : 'kolony-wordmark';
  const word = doc.createElement('div');
  word.className = 'kolony-wordmark-word';
  word.textContent = 'KOLONY';
  const row = doc.createElement('div');
  row.className = 'kolony-wordmark-row';
  const rule = (): HTMLDivElement => {
    const r = doc.createElement('div');
    r.className = 'kolony-wordmark-rule';
    return r;
  };
  const tagline = doc.createElement('div');
  tagline.className = 'kolony-wordmark-tagline';
  tagline.textContent = 'SETTLE · TRADE · BUILD'; // outline §1 generic words only (IP hygiene)
  if (mode === 'centred') row.append(rule(), tagline, rule());
  else row.append(rule(), tagline);
  w.append(word, row);
  return w;
}

/**
 * The monogram: amber rounded square + a hexagonal PRISM silhouette — our 3D board piece, drawn
 * as top face + two visible sides (deliberately a solid object, not a flat hex icon: outline §0
 * lists the original game's flat hex-icon mark as protected trade dress, so ours is the prism).
 * At 16px the side seams are dropped and the stroke steps up so the mark survives a favicon slot.
 */
export function monogram(doc: Document, size: 64 | 32 | 16): SVGSVGElement {
  const m = svgEl<SVGSVGElement>(doc, 'svg', { width: size, height: size, viewBox: '0 0 32 32' });
  const small = size === 16;
  m.append(svgEl(doc, 'rect', { width: 32, height: 32, rx: small ? 8 : 7, fill: '#ffb224' }));
  const sw = small ? 3 : size === 32 ? 2.2 : 2;
  // top face: a flattened hexagon (isometric), then the prism drops to a lower front edge
  const top = 'M16 7 L25 11.5 L25 14.5 L16 19 L7 14.5 L7 11.5 Z';
  m.append(svgEl(doc, 'path', { d: top, fill: 'none', stroke: '#0a1608', 'stroke-width': sw, 'stroke-linejoin': 'round' }));
  m.append(svgEl(doc, 'path', { d: 'M7 14.5 L7 20 L16 24.5 L25 20 L25 14.5', fill: 'none', stroke: '#0a1608', 'stroke-width': sw, 'stroke-linejoin': 'round' }));
  if (!small) {
    // the front seam of the prism + a pip dot on the top face (the number-disc affordance)
    m.append(svgEl(doc, 'path', { d: 'M16 19 L16 24.5', stroke: '#0a1608', 'stroke-width': sw, fill: 'none' }));
    m.append(svgEl(doc, 'circle', { cx: 16, cy: 12.6, r: 1.6, fill: '#0a1608' }));
  }
  return m;
}

export type IconName = 'person' | 'pencil' | 'sound-on' | 'sound-off';

/** Flat single-weight inline icons (stroke = currentColor, ink preset per glyph, overridable). */
export function svgIcon(doc: Document, name: IconName, size: number): SVGSVGElement {
  const i = svgEl<SVGSVGElement>(doc, 'svg', { width: size, height: size, viewBox: '0 0 16 16', fill: 'none' });
  if (name === 'sound-on' || name === 'sound-off') {
    i.style.color = 'currentColor';
    const speaker = { d: 'M3 6 L6 6 L9.5 3 L9.5 13 L6 10 L3 10 Z', stroke: 'currentColor', 'stroke-width': 1.5, 'stroke-linejoin': 'round' };
    i.append(svgEl(doc, 'path', speaker));
    if (name === 'sound-on') {
      i.append(svgEl(doc, 'path', { d: 'M11.7 5.6 A3.4 3.4 0 0 1 11.7 10.4', stroke: 'currentColor', 'stroke-width': 1.5, 'stroke-linecap': 'round' }));
      i.append(svgEl(doc, 'path', { d: 'M13.4 3.6 A6 6 0 0 1 13.4 12.4', stroke: 'currentColor', 'stroke-width': 1.5, 'stroke-linecap': 'round' }));
    } else {
      i.append(svgEl(doc, 'path', { d: 'M11.6 6.2 L14.6 9.8 M14.6 6.2 L11.6 9.8', stroke: 'currentColor', 'stroke-width': 1.5, 'stroke-linecap': 'round' }));
    }
    return i;
  }
  if (name === 'person') {
    i.style.color = 'var(--ink-2)';
    i.append(svgEl(doc, 'circle', { cx: 8, cy: 5.5, r: 3, stroke: 'currentColor', 'stroke-width': 1.5 }));
    i.append(svgEl(doc, 'path', { d: 'M2.5 14 C3.2 10.8 5.3 9.5 8 9.5 C10.7 9.5 12.8 10.8 13.5 14', stroke: 'currentColor', 'stroke-width': 1.5 }));
  } else {
    i.style.color = 'var(--ink-3)';
    i.append(
      svgEl(doc, 'path', {
        d: 'M11.5 2.5 L13.5 4.5 L5.5 12.5 L2.8 13.2 L3.5 10.5 Z',
        stroke: 'currentColor',
        'stroke-width': 1.5,
        'stroke-linejoin': 'round',
      }),
    );
  }
  return i;
}

/** An action link as a real <button> (keyboard + a11y). Amber by default; `muted` = ink-2 → amber. */
export function link(doc: Document, label: string, onClick: () => void, opts: { muted?: boolean } = {}): HTMLButtonElement {
  const l = doc.createElement('button');
  l.type = 'button';
  l.className = opts.muted ? 'kolony-link kolony-link--muted' : 'kolony-link';
  l.textContent = label;
  l.onclick = onClick;
  return l;
}
